import Text from "lib/components/Text"
import { useContext, useEffect, useState } from "react"
import Button from "lib/components/Button"
import useQueryParams from "hooks/useQueryParams"
import { LanguageContext } from "context/LanguageContext"
import InputPrice from "lib/components/InputPrice"

const priceRecommendations = [
  { min: 0, max: 10000, label: "Rp0 - Rp10.000" },
  { min: 10000, max: 100000, label: "Rp10.000 - Rp100.000" },
  { min: 100000, max: 500000, label: "Rp100.000 - Rp500.000" },
  { min: 500000, max: null, label: ">Rp500.000" },
]

interface PropsCardPublicPriceFilter {
  onChangeMinValue?: (value: number) => void
  onChangeMaxValue?: (value: number) => void
  handleSubmitFilterPrices?: (value: number, valueSec: number) => void
  handleResetPrice?: () => void
  withRangeLine?: boolean
  isDesktop?: boolean
  setOpenCard?: (value) => void
}
function CardPublicPriceFilter({
  handleSubmitFilterPrices,
  handleResetPrice,
  withRangeLine = true,
  isDesktop = true,
  setOpenCard,
}: PropsCardPublicPriceFilter) {
  const { useTranslation: t } = useContext(LanguageContext)
  const [minValuePricing, setMinValuePricing] = useState(null)
  const [maxValuePricing, setMaxValuePricing] = useState(null)
  const [selectedRecommendation, setSelectedRecommendation] = useState<number | null>(null)
  const queries = useQueryParams()

  const maxPriceValue = queries?.get("max_price")
  const minPriceValue = queries?.get("min_price")

  useEffect(() => {
    if (maxPriceValue || minPriceValue) {
      const maxPrice = maxPriceValue ? parseInt(maxPriceValue, 10) : 0
      const minPrice = minPriceValue ? parseInt(minPriceValue, 10) : 0
      setMaxValuePricing(maxPrice)
      setMinValuePricing(minPrice)
    } else {
      setMaxValuePricing("")
      setMinValuePricing("")
    }
  }, [maxPriceValue, minPriceValue])

  function validateInput() {
    let isReadyToApply = true
    const minPriceCheck = minValuePricing ? parseInt(minValuePricing, 10) : 0
    const maxPriceCheck = maxValuePricing ? parseInt(maxValuePricing, 10) : 0

    const minAndMaxMoreThanZero = () => {
      if (minPriceCheck > 0 && maxPriceCheck > 0) {
        return true
      }
      return false
    }

    const minPriceLessThanMaxPrice = () => {
      if (minPriceCheck < maxPriceCheck) {
        return true
      }
      return false
    }

    const minPriceMoreAndMaxLess = () => {
      if (minPriceCheck > 0 && maxPriceCheck <= 0) {
        return true
      }
      return false
    }

    const minPriceLessMaxPriceMoreThanZero = () => {
      if (minPriceCheck <= 0 && maxPriceCheck > 0) {
        return true
      }
      return false
    }

    if (minAndMaxMoreThanZero()) {
      if (minPriceLessThanMaxPrice()) {
        isReadyToApply = false
      }
    }

    if (minPriceMoreAndMaxLess()) {
      isReadyToApply = false
    }
    if (minPriceLessMaxPriceMoreThanZero()) {
      isReadyToApply = false
    }

    return isReadyToApply
  }
  return (
    <section className="top-0 left-0 right-0 bottom-0 flex h-full w-full overflow-y-auto bg-opacity-80 lg:p-4">
      <div className="flex w-full flex-col justify-between rounded-md bg-white border lg:shadow-lg">
        {/* serach categories */}
        <div className="px-3 pt-3 lg:block hidden">
          <p className="font-bold text-sm leading-5">{t("price")}</p>
        </div>
        {/* content */}
        <div className="bg-info-border/20 rounded-lg p-3 mx-3 my-3">
          <div className="flex flex-row space-x-3 overflow-hidden">
            <div className="mr-auto flex h-11 w-full items-center rounded-lg border bg-white">
              <InputPrice
                name="min_value"
                placeholder="0"
                value={minValuePricing}
                onChange={(value) => {
                  setSelectedRecommendation(null)
                  setMinValuePricing(value)
                }}
                noBorder
              />
              <div className="mr-3 text-gray-600 hover:text-gray-900">
                <Text size="small" color="muted" weight="medium">
                  Min.
                </Text>
              </div>
            </div>
            <div className="mr-auto flex h-11 w-full items-center rounded-lg border bg-white">
              <InputPrice
                name="max_value"
                placeholder="0"
                value={maxValuePricing}
                onChange={(value) => {
                  setSelectedRecommendation(null)
                  setMaxValuePricing(value)
                }}
                noBorder
              />
              <div className="mr-3 text-gray-600 hover:text-gray-900">
                <Text size="small" color="muted" weight="medium">
                  Max.
                </Text>
              </div>
            </div>
          </div>
        </div>
        <div className="px-3 lg:block hidden">
          <Text weight="bold">{t("recommendation")}</Text>
        </div>
        <div className="flex flex-wrap justify-center gap-2 lg:px-1 mt-2 w-full">
          {priceRecommendations.map((range, idx) => (
            <button
              type="button"
              key={idx}
              className={`border rounded-lg px-4 py-2 text-sm ${
                selectedRecommendation === idx
                  ? "border-primary bg-primary-surface text-primary"
                  : "border-neutral-40 text-gray-700 hover:bg-neutral-20"
              } ${idx === 2 && "-ml-2"}`}
              onClick={() => {
                setSelectedRecommendation(idx)
                setMinValuePricing(range.min)
                setMaxValuePricing(range.max ?? "")
              }}>
              {range.label}
            </button>
          ))}
        </div>
        {withRangeLine && (
          <div className="lg:px-3 mt-3 w-full">
            <input
              type="range"
              min="0"
              max="1000000"
              value={maxValuePricing}
              defaultValue={maxValuePricing}
              step="5000"
              className="appearance-none w-full h-2 rounded-full bg-neutral-20 outline-none cursor-pointer"
              list="tickmarks"
              onChange={(event) => {
                const value = parseInt(event.target.value, 10)
                setMaxValuePricing(value)
              }}
            />
          </div>
        )}
        {/* footer */}
        <div className="mt-3 flex-row items-center border-t px-3 py-3 flex">
          <Button
            variant="text"
            color="white"
            colorText="muted"
            fontWeight="semibold"
            disabled={validateInput()}
            onClick={() => {
              setMaxValuePricing("")
              setMinValuePricing("")
              setSelectedRecommendation(null)
              if (isDesktop) {
                if (maxPriceValue || minPriceValue) {
                  handleResetPrice()
                }
              } else {
                handleSubmitFilterPrices(null, null)
              }
            }}
            customClass={`w-10 ${!validateInput() && "!text-danger"}`}>
            {t("reset")}
          </Button>
          <div className="flex w-full items-end justify-end">
            <Button
              variant="contained"
              color="primary"
              rounded="large"
              disabled={validateInput()}
              onClick={() => {
                handleSubmitFilterPrices(minValuePricing, maxValuePricing)
                if (!isDesktop) {
                  setOpenCard(false)
                }
              }}
              customClass="flex flex-wrap justify-center w-28 px-4 py-2.5 hover:bg-primary font-medium text-base text-white border border-primary rounded-md shadow-button">
              {t("apply")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CardPublicPriceFilter
